import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Smartphone, Wifi, Tv, Phone, ShieldCheck, Radio,
  Network, Building2, LayoutGrid, Lock, Monitor, Cloud,
  Zap, Antenna, Star, X, CheckCircle2, Lightbulb, Briefcase, TrendingUp
} from 'lucide-react';
import { useSmoothReveal } from '../utils/useScrollAnimations';

const Services = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const sectionRef = useRef(null);
  
  const titleReveal = useSmoothReveal({ fadeDirection: 'up', threshold: 0.05, rootMargin: '50px' });
  const cardsReveal = useSmoothReveal({ fadeDirection: 'up', threshold: 0.05, rootMargin: '50px' });

  // Zavřít modal klávesou Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setSelectedService(null);
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Zablokovat scroll při otevřeném modalu
  useEffect(() => {
    document.body.style.overflow = selectedService ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedService]);

  const allServices = [
    {
      Icon: Smartphone,
      title: 'Mobilní služby',
      description: 'Komplexní mobilní tarify a řešení pro firmy',
      color: 'from-green-500 to-green-600',
      benefits: ['Firemní tarify', 'Vodafone síť'],
      detail: {
        intro: 'Firemní mobilita na jedné faktuře, s jedním kontaktem a tarify, které vám jako jednotlivci nikdy nenabídnou.',
        what: [
          'Firemní SIM karty s neomezenými hovory a daty v síti Vodafone',
          'Správa více SIM karet z jednoho portálu (MDM – Mobile Device Management)',
          'eSIM pro moderní zařízení bez fyzické výměny karty',
          'Datové balíčky sdílené napříč celou firmou',
          'Roaming v EU i mimo EU za předem domluvené ceny',
          'Firemní mobilní internet (MBB) pro tablety, notebooky a pojistné linky',
        ],
        how: [
          'Centralizovaná správa šetří čas IT oddělení i managementu',
          'Sdílené datové balíčky eliminují přečerpání u jednoho uživatele',
          'Jedno vyúčtování za celou firmu místo desítek individuálních smluv',
          'Možnost nastavit limity a blokace konkrétním uživatelům',
        ],
        why: 'Naši klienti průměrně ušetří 20–35 % oproti individuálním tarifům. Zajistíme přechod bez výpadku a bez papírování — celou změnu vyřídíme za vás.',
        industries: ['Logistika a doprava', 'Obchodní firmy a terénní technici', 'Stavebnictví', 'Zdravotnictví', 'Maloobchod a franchise'],
      },
    },
    {
      Icon: Wifi,
      title: 'Pevný internet',
      description: 'Rychlé a stabilní internetové připojení pro firmy',
      color: 'from-red-500 to-red-600',
      benefits: ['Až 2000 Mb/s', 'Stabilní připojení'],
      detail: {
        intro: 'Stabilní, symetrické připojení s garancí dostupnosti — základ pro cloudové aplikace, VoIP a videokonference bez sekání.',
        what: [
          'Optické připojení (FTTO) s rychlostí až 2 Gb/s',
          'Symetrické linky pro nahrávání i stahování stejnou rychlostí',
          'Záložní připojení přes LTE/5G při výpadku primární linky',
          'Wi-Fi infrastruktura (access pointy, kontrolery)',
          'Instalace a konfigurace celého LAN/WAN prostředí',
          'Možnost přidat statické IP adresy nebo celé IP bloky',
        ],
        how: [
          'Cloudové aplikace (Microsoft 365, SAP, CRM) fungují bez zpoždění',
          'VoIP hovory a videokonference bez výpadků a šumu',
          'Backup linka zajistí provoz i při poruše primárního spoje',
          'Symetrie je klíčová při sdílení souborů a videí do cloudu',
        ],
        why: 'Domluvíme SLA s garancí dostupnosti 99,5 % a smluvní kompenzací při výpadku. Jeden partner pro Internet, Wi-Fi i celou síťovou infrastrukturu.',
        industries: ['IT firmy a softwarové agentury', 'Call a kontaktní centra', 'Hotely a ubytovací zařízení', 'Výrobní podniky', 'Kancelářské budovy a coworking'],
      },
    },
    {
      Icon: Tv,
      title: 'Televize',
      description: 'Moderní televizní služby s bohatou nabídkou programů',
      color: 'from-indigo-500 to-indigo-600',
      benefits: ['4K kvalita', 'Vodafone TV+'],
      detail: {
        intro: 'Firemní televize pro recepce, čekárny, restaurace i zaměstnanecké zóny — moderní IP TV bez satelitního talíře.',
        what: [
          'Vodafone TV+ s přístupem k prémiovým kanálům a 4K obsahu',
          'IP TV přes stávající síťovou infrastrukturu (bez antén)',
          'Nahrávání pořadů a catch-up TV (zpětné přehrávání)',
          'Správa více televizorů z jednoho účtu',
          'Možnost streamovat firemní obsah nebo prezentace na TV obrazovky',
        ],
        how: [
          'Zákazníci v čekárnách a lobby jsou lépe naladěni díky kvalitnímu obsahu',
          'Úspora za satelitní nebo kabelový rozvod v budově',
          'Balíčkové ceny s internetem nebo mobilními službami',
        ],
        why: 'Výhodné při odběru v balíčku s pevným internetem — jedna faktura, jedna smlouva, jeden kontakt.',
        industries: ['Hotely a wellness centra', 'Restaurace a kavárny', 'Čekárny lékařů a klinik', 'Fitness centra', 'Showroomy a prodejny'],
      },
    },
    {
      Icon: Phone,
      title: 'Pevná linka',
      description: 'Spolehlivé telefonní služby pro každodenní komunikaci',
      color: 'from-indigo-500 to-indigo-600',
      benefits: ['Nízké náklady', 'VoIP'],
      detail: {
        intro: 'Moderní přechod ze starých analogových linek na VoIP — bez výpadku provozu, s nižšími náklady a lepší integrací.',
        what: [
          'VoIP přípojky (SIP trunky) kompatibilní s většinou ústředen',
          'DID čísla (přímé volání na konkrétního pracovníka nebo oddělení)',
          'Zachování stávajících telefonních čísel při přechodu',
          'Integrace s Microsoft Teams, Cisco, Avaya a dalšími systémy',
          'Konferenční hovory bez externího nástroje',
        ],
        how: [
          'Náklady na telefonii klesají o 30–50 % přechodem z ISDN/PSTN na VoIP',
          'Zaměstnanci mohou přijímat firemní hovory odkudkoli přes aplikaci',
          'Snadná správa čísel a přesměrování bez techniků',
        ],
        why: 'Přechod zvládneme bez ztráty čísla a bez výpadku — migraci řídíme my, vy jen podepíšete a pracujete dál.',
        industries: ['Kanceláře všech velikostí', 'Právní a poradenské firmy', 'Účetní a daňová studia', 'Veřejná správa', 'Obchodní zástupci'],
      },
    },
    {
      Icon: ShieldCheck,
      title: 'Základní zabezpečení',
      description: 'Antivirus a firewall pro ochranu dat',
      color: 'from-red-500 to-red-600',
      benefits: ['Ochrana dat', 'Snadná správa'],
      detail: {
        intro: 'Základ kybernetické ochrany pro každou firmu — antivirus, firewall a DNS filtr, centrálně spravované z cloudu.',
        what: [
          'Podnikový antivirus a anti-malware pro všechna zařízení (ESET, Bitdefender, Microsoft Defender)',
          'Next-generation firewall pro filtrování síťového provozu',
          'DNS filtr blokující škodlivé weby ještě před načtením',
          'Centrální cloudová konzole pro správu všech endpointů',
          'Automatické aktualizace a patching bez zásahu uživatele',
          'Cloudové zálohování firemních dat',
        ],
        how: [
          'Ransomware a phishing jsou blokovány dříve, než způsobí škodu',
          'GDPR compliance — ochrana osobních dat zákazníků a zaměstnanců',
          'IT tým nebo správce vidí stav všech zařízení v jednom dashboardu',
        ],
        why: 'Průměrná škoda ransomware útoku na SMB firmu je 200 000–500 000 Kč. Měsíční náklady na ochranu jsou zlomkem toho.',
        industries: ['Všechny firmy od 5 zaměstnanců', 'E-shopy a e-commerce', 'Zdravotnická zařízení', 'Účetní a právní firmy', 'Výrobní podniky'],
      },
    },
    {
      Icon: Radio,
      title: 'IoT řešení',
      description: 'Internet věcí pro automatizaci a monitoring',
      color: 'from-teal-500 to-teal-600',
      benefits: ['Vozový park', 'eDohled'],
      detail: {
        intro: 'Propojte svá vozidla, stroje a objekty do jednoho systému — sledujte, automatizujte a reagujte v reálném čase.',
        what: [
          'GPS sledování vozového parku (poloha, trasy, stání, řidičské chování)',
          'Telemedicínské a průmyslové IoT SIM karty s globálním pokrytím',
          'Dálkové odečty energií, plynu a vody (smart metering)',
          'Vzdálený monitoring strojů a výrobních linek',
          'eDohled a bezpečnostní kamery přes mobilní síť',
          'Smart zámky, přístupové systémy a automatizace budov',
        ],
        how: [
          'Snížení nákladů na palivo díky optimalizaci tras a chování řidiče',
          'Prevence krádeží vozidel a vybavení',
          'Prediktivní údržba strojů díky senzorům — žádné neplánované výpadky',
          'Vzdálená správa bez fyzické přítomnosti technika',
        ],
        why: 'Zákazníci v logistice průměrně ušetří 15–25 % nákladů na provoz vozového parku. IoT se vrátí do 6–12 měsíců.',
        industries: ['Logistika a doprava', 'Stavebnictví a strojní provozy', 'Energetika a utility', 'Zemědělství (smart farming)', 'Bezpečnostní agentury a správa objektů'],
      },
    },
    {
      Icon: Network,
      title: 'SD-WAN',
      description: 'Zabezpečené a optimalizované síťové připojení',
      color: 'from-red-600 to-red-700',
      benefits: ['Optimalizace sítě', 'Centrální správa'],
      detail: {
        intro: 'Nahraďte drahé MPLS linky inteligentní softwarovou sítí — nižší náklady, lepší výkon, centrální správa ze všech poboček najednou.',
        what: [
          'Softwarově definovaná WAN síť přes standardní internetové připojení',
          'Nahrazení nebo doplnění MPLS při zachování bezpečnosti a QoS',
          'Automatická prioritizace aplikací (Teams, SAP, VoIP mají přednost)',
          'Centrální správa všech poboček z jediné konzole',
          'Šifrovaný tunel mezi pobočkami bez potřeby HW VPN',
          'Rychlé zapojení nových poboček bez návštěvy technika',
        ],
        how: [
          'Kritické aplikace jako SAP nebo Teams fungují bez zpoždění i na pomalé lince',
          'Výpadek jednoho poskytovatele internetu automaticky přepne na zálohu',
          'IT oddělení spravuje všechny pobočky odkudkoli přes webové rozhraní',
        ],
        why: 'Úspora 40–60 % oproti tradičnímu MPLS. Nová pobočka je aktivní za hodiny, ne týdny. Ideální pro firmy s více než 3 pobočkami.',
        industries: ['Retailové a obchodní řetězce', 'Banky a pojišťovny', 'Restaurační a hotelové řetězce', 'Firmy s distribuovanými pobočkami', 'Průmyslové holdingsové skupiny'],
      },
    },
    {
      Icon: Building2,
      title: 'Virtuální ústředny',
      description: 'Pokročilé telefonní systémy pro firmy',
      color: 'from-indigo-500 to-indigo-600',
      benefits: ['Neomezená kapacita', 'Pokročilé funkce'],
      detail: {
        intro: 'Cloudová telefonní ústředna bez HW investic — zaměstnanci dostupní na jednom čísle odkudkoli, včetně práce z domova.',
        what: [
          'Cloud PBX — virtuální ústředna bez fyzického HW na místě',
          'Microsoft Teams Direct Routing pro hovory přímo v Teams',
          'IVR (hlasový strom) a automatická distribuce hovorů (ACD)',
          'Nahrávání hovorů pro call centra a kontrolu kvality',
          'Statistiky a reporting hovorů v reálném čase',
          'Mobilní a desktopová aplikace pro přijímání hovorů odkudkoli',
          'Integrace s CRM (Salesforce, HubSpot, vlastní CRM)',
        ],
        how: [
          'Zaměstnanec přijímá firemní hovory doma, v terénu nebo v kanceláři',
          'Zákazník vždy dovolá — přesměrování na mobilní zálohu při nedostupnosti',
          'Call centrum zvládne špičky bez nutnosti kupovat HW kapacity',
        ],
        why: 'Žádné počáteční investice do HW ústředny (100–500 000 Kč). Platíte jen za aktivní uživatele, kapacita roste se zákazníky.',
        industries: ['Call a kontaktní centra', 'Pojišťovny a finanční poradci', 'Zdravotnictví a kliniky', 'Obchodní firmy s terénem', 'Firmy s home office zaměstnanci'],
      },
    },
    {
      Icon: LayoutGrid,
      title: 'Microsoft 365 licence',
      description: 'Kompletní produktivní suite pro moderní firmy',
      color: 'from-red-500 to-red-600',
      benefits: ['Office aplikace', 'Teams komunikace'],
      detail: {
        intro: 'Správné Microsoft 365 licence za správnou cenu — poradíme vám s výběrem plánu, zajistíme migraci a zůstaneme vaším technickým kontaktem.',
        what: [
          'Licencování M365 Business Basic, Standard, Premium nebo Enterprise',
          'Migrace emailů a dat z Google Workspace, Exchange nebo lokálních serverů',
          'Nastavení Microsoft Teams (chat, videokonference, sdílení)',
          'SharePoint pro sdílení dokumentů a firemní intranet',
          'Microsoft Intune — správa a zabezpečení firemních zařízení',
          'Azure Active Directory pro jednotné přihlášení (SSO)',
          'Průběžná technická podpora a správa licencí',
        ],
        how: [
          'Zaměstnanci spolupracují v reálném čase odkudkoli',
          'Firemní data jsou v bezpečném cloudu Microsoftu, ne na lokálním serveru',
          'IT správce vidí a spravuje všechna zařízení z jednoho místa (Intune)',
        ],
        why: 'Optimalizace licenčního mixu průměrně šetří 15–25 % nákladů. Správná licence zajistí soulad s GDPR a auditní stopu.',
        industries: ['Firmy od 5 do 5 000 uživatelů', 'Školy a vzdělávací instituce', 'Neziskové organizace', 'Poradenské a konzultační firmy', 'Výrobní podniky migrující z papíru'],
      },
    },
    {
      Icon: Lock,
      title: 'Kyberbezpečnost',
      description: 'Komplexní ochrana proti kybernetickým hrozbám',
      color: 'from-red-600 to-red-700',
      benefits: ['ISO 27001', 'Proaktivní monitoring'],
      detail: {
        intro: 'Vícevrstvá ochrana pro firmy, které si nemohou dovolit bezpečnostní incident — od penetračního testu až po 24/7 monitoring.',
        what: [
          'Penetrační testy infrastruktury a webových aplikací',
          'SOC as a Service — bezpečnostní operační centrum jako služba (24/7)',
          'SIEM — sběr a analýza bezpečnostních logů v reálném čase',
          'Simulace phishingových útoků a školení zaměstnanců',
          'Příprava na ISO 27001 a NIS2 certifikaci',
          'Incident response — rychlá reakce při bezpečnostním incidentu',
          'Vulnerability management — pravidelné skenování zranitelností',
        ],
        how: [
          'Hrozby jsou detekovány a neutralizovány dříve, než způsobí škodu',
          'Audit připraví firmu na zákonné požadavky (NIS2, GDPR, ISO 27001)',
          'Zaměstnanci po školení rozpoznají phishing a sociální inženýrství',
        ],
        why: 'Od 2024 je NIS2 zákonná povinnost pro stovky kategorií firem. Kybernetický incident průměrně stojí 1–5 milionů Kč včetně reputační škody.',
        industries: ['Banky a finanční instituce', 'Zdravotnictví a nemocnice', 'Státní správa a samospráva', 'E-commerce a platební systémy', 'Energetika a kritická infrastruktura'],
      },
    },
    {
      Icon: Monitor,
      title: 'Managed IT',
      description: 'Kompletní správa IT infrastruktury',
      color: 'from-yellow-500 to-yellow-600',
      benefits: ['Proaktivní monitoring', 'Specializovaný tým'],
      detail: {
        intro: 'Outsourcujte celé IT oddělení nebo ho doplňte — váš tým se soustředí na byznys, IT problémy řeší naši specialisté.',
        what: [
          'Remote helpdesk — podpora uživatelů telefonem, chatem nebo vzdáleným přístupem',
          'Správa serverů, workstací a síťových prvků',
          'Automatický patching a aktualizace operačních systémů',
          '24/7 proaktivní monitoring s alertingem',
          'Onsite podpora při fyzické závadě (SLA 4h, 8h, NBD)',
          'Správa zálohování a disaster recovery plánů',
          'Asset management — evidence veškerého HW a SW majetku',
        ],
        how: [
          'Výpadky jsou řešeny proaktivně ještě před tím, než je zaznamenají uživatelé',
          'Firma platí jen za skutečně využitou kapacitu, ne za interního IT pracovníka',
          'Garantované SLA zaručuje, že problém bude vyřešen v definovaném čase',
        ],
        why: 'Managed IT je průměrně o 30–40 % levnější než plný interní IT pracovník — bez rizika výpadku při nemoci nebo dovolené.',
        industries: ['SMB firmy 10–200 zaměstnanců', 'Advokátní kanceláře', 'Účetní a daňové firmy', 'Realitní kanceláře', 'Výrobní podniky bez IT oddělení'],
      },
    },
    {
      Icon: Cloud,
      title: 'Cloud řešení',
      description: 'Škálovatelná cloudová infrastruktura',
      color: 'from-cyan-500 to-cyan-600',
      benefits: ['Vysoká dostupnost', 'Automatické zálohování'],
      detail: {
        intro: 'Migrace do cloudu bez bolesti — od plánování přes realizaci až po provoz. Azure, AWS nebo privátní cloud na míru.',
        what: [
          'Cloud migrace lokálních serverů a aplikací do Azure / AWS / Google Cloud',
          'IaaS — virtuální servery, storage a síť v cloudu',
          'Cloudové zálohování a disaster recovery (RPO/RTO dle potřeby)',
          'Hybridní cloud — kombinace cloudu a on-premise prostředí',
          'Optimalizace cloudových nákladů (rightsizing, rezervované instance)',
          'Containerizace a Kubernetes pro moderní aplikace',
        ],
        how: [
          'Servery v cloudu mají garantovanou dostupnost 99,9 %+ bez vlastního HW',
          'Kapacita roste automaticky se zátěží — platíte jen za to, co spotřebujete',
          'Zálohy v geograficky oddělené lokalitě chrání před výpadkem celého místa',
        ],
        why: 'Průměrná firma ušetří 20–35 % IT nákladů přechodem z fyzických serverů do cloudu — odpadají HW investice, elektřina a fyzická správa.',
        industries: ['SaaS startupy a technologické firmy', 'E-commerce s proměnlivou zátěží', 'Zdravotnictví (GDPR-compliant cloud)', 'Vzdělávání a e-learning platformy', 'Firmy s požadavky na disaster recovery'],
      },
    },
    {
      Icon: Zap,
      title: 'Garantované připojení',
      description: 'Spolehlivé připojení s garantovanou dostupností',
      color: 'from-orange-500 to-orange-600',
      benefits: ['SLA garance', '99.9% dostupnost'],
      detail: {
        intro: 'Dedikovaná linka výhradně pro vaši firmu — žádné sdílení kapacity, garantovaná rychlost a finanční kompenzace při výpadku.',
        what: [
          'Dedikovaná optická linka (leased line) výhradně pro jednoho zákazníka',
          'Symetrické rychlosti od 10 Mb/s do 10 Gb/s',
          'SLA s garancí dostupnosti 99,95 % a smluvní kompenzací',
          'BGP dual-homing — záloha přes druhého operátora',
          'Garantovaná přenosová kapacita (žádný throttling ani sdílení)',
          'Prioritní technická podpora s dobou opravy do 4 hodin',
        ],
        how: [
          'Mise-kritické aplikace (ERP, výroba, trading) nikdy nepocítí výpadek',
          'Garantovaná latence je klíčová pro real-time systémy a VoIP',
          'Finanční kompenzace motivuje operátora k rychlé opravě',
        ],
        why: 'Pro firmy, kde hodina výpadku stojí více než měsíční náklady na garantované připojení. Výpadek dat. centra nebo nemocnice je nepřijatelný.',
        industries: ['Datová centra a colo providery', 'Nemocnice a zdravotnická zařízení', 'Banky a obchodní platformy', 'Průmyslová výroba (Industry 4.0)', 'Velká kancelářská sídla a kampusy'],
      },
    },
    {
      Icon: Antenna,
      title: '5G Private Network',
      description: 'Privátní 5G síť pro kritické aplikace',
      color: 'from-emerald-500 to-emerald-600',
      benefits: ['Ultra nízká latence', 'Vysoká bezpečnost'],
      detail: {
        intro: 'Vlastní 5G síť uvnitř vašeho areálu — neovlivní ji nikdo zvenčí, latence pod 5 ms a stovky simultánně připojených zařízení.',
        what: [
          'Privátní 5G campus síť provozovaná výhradně pro váš areál',
          'Ultra nízká latence (< 5 ms) pro real-time průmyslové aplikace',
          'Vysoká hustota zařízení — tisíce senzorů, robotů a kamer',
          'Edge computing — výpočty probíhají lokálně, ne v cloudu',
          'Integrace s průmyslovými systémy (SCADA, MES, ERP)',
          'Izolovaná síť — kritická data neopustí areál',
        ],
        how: [
          'AGV roboti a autonomní vozíky komunikují v reálném čase bez zpoždění',
          'AR/VR pro techniky a operátory funguje plynule i při vysoké zátěži',
          'Kritická bezpečnostní komunikace (záchranáři, průmysl) bez výpadku',
        ],
        why: 'Industry 4.0 a automatizace výroby vyžadují síť, která nestojí na sdílené veřejné infrastruktuře. Privátní 5G je budoucnost průmyslové konektivity.',
        industries: ['Průmyslová výroba a automobilový průmysl', 'Logistické sklady a distribučních centra', 'Přístavy a letiště', 'Stadiony a velké areály', 'Hornická a těžební průmysl'],
      },
    },
    {
      Icon: Star,
      title: 'Individuální řešení',
      description: 'Řešení na míru podle specifických potřeb',
      color: 'from-pink-500 to-pink-600',
      benefits: ['Osobní přístup', 'Flexibilní podmínky'],
      detail: {
        intro: 'Nestandardní požadavek? Právě tam vynikáme. Analyzujeme vaši situaci, oslovíme správné dodavatele a celý projekt za vás vyjednáme.',
        what: [
          'Detailní analýza aktuálního IT a telco stavu firmy',
          'Výběrové řízení na dodavatele a vyjednání podmínek smlouvy',
          'Projektový management komplexní IT/telco transformace',
          'Integrace více dodavatelů do jednoho funkčního řešení',
          'Tendr na specifické technologie nebo poskytovatele',
          'Konzultace při M&A — slučování IT infrastruktur dvou firem',
        ],
        how: [
          'Ušetříte čas vlastního IT a nákupního oddělení',
          'Nezávislé poradenství — pracujeme pro vás, ne pro konkrétního dodavatele',
          'Zkušenosti z desítek projektů zabraňují typickým chybám',
        ],
        why: 'Jeden zkušený partner je levnější než tým interních specialistů. Naše síť dodavatelů a zkušenosti z vyjednávání přinášejí podmínky, které sami nezískáte.',
        industries: ['Firmy procházející digitální transformací', 'Holdingové skupiny s více dceřinými firmami', 'Firmy po akvizici', 'Startupy škálující infrastrukturu', 'Firmy s komplexními regulatorními požadavky'],
      },
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // Komponenta pro jednotlivé služby
  const ServiceCard = ({ service, index, delay = 0 }) => {
    const { Icon } = service;
    return (
    <div
      className={`bg-white rounded-xl p-4 sm:p-6 text-center transform transition-all duration-700 hover:scale-105 hover:-translate-y-1 hover:shadow-2xl group h-full flex flex-col border border-gray-100 hover:border-red-100 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
      onClick={() => setSelectedService(service)}
    >
      <div className="flex flex-col h-full">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-md`}>
          <Icon size={22} className="text-white" />
        </div>
        <h4 className="text-base sm:text-lg font-semibold mb-2 text-gray-900">
          {service.title}
        </h4>
        <p className="text-sm mb-3 sm:mb-4 leading-relaxed flex-grow text-gray-500">
          {service.description}
        </p>
        {service.benefits && (
          <div className="space-y-1 mb-3">
            {service.benefits.map((benefit, i) => (
              <div key={i} className="text-xs sm:text-sm text-green-600 flex items-center justify-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                {benefit}
              </div>
            ))}
          </div>
        )}
        <div className="mt-auto pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold text-red-600 group-hover:text-red-700 flex items-center justify-center gap-1 transition-colors">
            Více informací
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </span>
        </div>
      </div>
    </div>
  );
  };

  // Slide-in modal pro detail služby
  const ServiceDetailModal = ({ service, onClose }) => {
    const { Icon, detail } = service;
    if (!detail) return null;
    return ReactDOM.createPortal(
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* Panel */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={service.title}
          className="fixed top-0 right-0 h-full w-full sm:w-[520px] max-w-full z-50 bg-white shadow-2xl overflow-y-auto flex flex-col"
          style={{ animation: 'slideInRight 0.32s cubic-bezier(0.32,0.72,0,1)' }}
        >
          {/* Header */}
          <div className={`bg-gradient-to-br ${service.color} p-6 sm:p-8 flex-shrink-0`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon size={28} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{service.title}</h2>
                  <p className="text-sm text-white/80 mt-0.5">{service.description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Zavřít"
              >
                <X size={18} className="text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8 flex-grow space-y-7">
            {/* Intro */}
            <p className="text-gray-700 text-base leading-relaxed border-l-4 border-red-500 pl-4 italic">
              {detail.intro}
            </p>

            {/* Co zařídíme */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-red-600 flex-shrink-0" />
                <h3 className="font-bold text-gray-900 text-base">Co pro vás zařídíme</h3>
              </div>
              <ul className="space-y-2">
                {detail.what.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Jak to pomůže */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb size={18} className="text-amber-500 flex-shrink-0" />
                <h3 className="font-bold text-gray-900 text-base">Jak vám to pomůže</h3>
              </div>
              <ul className="space-y-2">
                {detail.how.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Proč vzít */}
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-red-600 flex-shrink-0" />
                <h3 className="font-bold text-red-700 text-base">Proč to pořídit</h3>
              </div>
              <p className="text-sm text-red-800 leading-relaxed">{detail.why}</p>
            </div>

            {/* Obory */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Briefcase size={18} className="text-gray-500 flex-shrink-0" />
                <h3 className="font-bold text-gray-900 text-base">Typické obory využití</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.industries.map((ind, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1 font-medium">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="p-6 sm:p-8 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={() => {
                onClose();
                setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-lg shadow-red-500/20"
            >
              Získat nabídku pro {service.title}
            </button>
          </div>
        </div>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
      </>,
      document.body
    );
  };

  return (
    <>
    <section
      ref={sectionRef}
      id="services"
      className="py-12 md:py-20 relative bg-gradient-to-b from-white via-red-50/30 to-gray-50"
    >
      {/* Background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-1/4 w-32 h-32 bg-red-500 rounded-full filter blur-xl"></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-red-600 rounded-full filter blur-xl"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Hlavní nadpis */}
        <div
          ref={titleReveal.ref}
          className="text-center mb-12 md:mb-16"
          style={titleReveal.animationStyle}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 text-gray-900">
            <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
              Vyjednáme řešení
            </span>
          </h2>
          <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto mb-6 md:mb-8"></div>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto px-4 text-gray-600">
            Zařídíme Vám komplexní telco a ICT služby s osobním přístupem a kvalitou
          </p>
        </div>

        {/* Základní služby */}
        <div 
          ref={cardsReveal.ref}
          className="mb-12 md:mb-16"
          style={cardsReveal.animationStyle}
        >
          <div className="text-center mb-6 md:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-red-600">
              Základní služby
            </h3>
            <p className="text-base sm:text-lg px-4 text-gray-600">
              Nezbytné služby pro každodenní provoz
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 max-w-7xl mx-auto mb-8 md:mb-12">
            {allServices.slice(0, 5).map((service, index) => (
              <ServiceCard 
                key={index} 
                service={service} 
                index={index} 
                delay={300 + index * 100}
              />
            ))}
          </div>
        </div>

        {/* Business řešení */}
        <div className="mb-12 md:mb-16">
          <div className={`text-center mb-6 md:mb-8 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-purple-600">
              Business řešení
            </h3>
            <p className="text-base sm:text-lg px-4 text-gray-600">
              Profesionální služby pro rostoucí firmy
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 max-w-7xl mx-auto mb-8 md:mb-12">
            {allServices.slice(5, 10).map((service, index) => (
              <ServiceCard 
                key={index + 5} 
                service={service} 
                index={index + 5} 
                delay={600 + index * 100}
              />
            ))}
          </div>
        </div>

        {/* Pokročilé technologie */}
        <div className="mb-8 md:mb-16">
          <div className={`text-center mb-6 md:mb-8 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-emerald-600">
              Pokročilé technologie
            </h3>
            <p className="text-base sm:text-lg px-4 text-gray-600">
              Inovativní řešení pro budoucnost
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {allServices.slice(10, 15).map((service, index) => (
              <ServiceCard 
                key={index + 10} 
                service={service} 
                index={index + 10} 
                delay={900 + index * 100}
              />
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className={`text-center mt-16 md:mt-20 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-red-50 to-white border border-red-200 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto shadow-sm">
            <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">
              Nevíte, které řešení je pro vás nejvhodnější?
            </h4>
            <p className="text-base sm:text-lg mb-4 sm:mb-6 px-4 text-gray-600">
              Kontaktujte nás a my vám připravíme nezávaznou analýzu a doporučení na míru
            </p>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-500/25"
            >
              Získat konzultaci zdarma
            </button>
          </div>
        </div>
      </div>
    </section>

    {/* Slide-in detail modal */}
    {selectedService && (
      <ServiceDetailModal
        service={selectedService}
        onClose={() => setSelectedService(null)}
      />
    )}
  </>
  );
}
export default Services;

